/**
 * TemperSkillEffect — Fire tree Ember Fork 2-A: permanent burn applicator.
 *
 * Touch a burning enemy at melee range and their burn becomes permanent — the
 * DoT never expires until they die. Quiet, surgical, devastating. The target
 * must already carry `burning`; Temper doesn't ignite, it transforms.
 *
 * On success, applies the `tempered` marker status (permanent, durationMs -1)
 * to the target. This marker is consumed by downstream upgrades:
 * - Branded (`fireResistanceReduction`): also applies `fire_resistance_debuff`
 * - Contagion (`transferRadiusTiles`): on Tempered death, permanent burn jumps
 *   to the nearest burning enemy within range. Chains indefinitely.
 * - Scar: DeathTriggerSystem increments run-scoped kill counter
 *
 * Activation flow:
 * 1. Cooldown check (no cooldown consumed if on cooldown)
 * 2. Melee range check (no cooldown consumed if out of range)
 * 3. Burning precondition (cooldown consumed even on failure)
 * 4. Fizzle roll (cooldown consumed even on fizzle)
 * 5. Make burning permanent + apply tempered marker + upgrade hooks
 *
 * @see fire-spec.md section Temper (`temper`)
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Death context (subset of DeathContext relevant to Contagion upgrade)
// ---------------------------------------------------------------------------

export interface TemperDeathContext {
  /** Status effect IDs active on the dying enemy at time of death. */
  readonly activeStatuses: string[];
  /** World position of the death (for spatial range queries). */
  readonly deathPosition: { x: number; y: number };
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface TemperDeps {
  /** Returns true if a valid target is within melee range (1 tile). */
  readonly isInMeleeRange: () => boolean;
  /** Returns true if the skill activation succeeds (false = fizzle). */
  readonly rollSkillSuccess: (skillId: string) => boolean;
  /** Returns true if the melee-range target carries the given status. */
  readonly hasTargetStatus: (statusId: string) => boolean;
  /** Converts the target's existing burning DoT to permanent (remainingMs = -1). */
  readonly makeBurningPermanent: () => void;
  /** Applies a status effect to the melee-range target. */
  readonly applyTargetStatus: (
    statusId: string,
    opts: { durationMs: number; sourceSkillId: string },
  ) => void;
  readonly resolver: {
    hasEffect(skillId: string, key: string): boolean;
    getAdditiveEffect?(skillId: string, key: string): number;
  };
  // --- Contagion deps (optional — only wired when temper_spread is unlocked) ---
  /**
   * Find the nearest enemy within `radiusTiles` of `position` that carries
   * the given status. Returns an opaque enemy identifier, or null if no
   * valid target exists.
   */
  readonly findNearestEnemyWithStatus?: (
    position: { x: number; y: number },
    statusId: string,
    radiusTiles: number,
  ) => string | null;
  /** Converts a specific enemy's burning DoT to permanent (remainingMs = -1). */
  readonly makeBurningPermanentOnEnemy?: (enemyId: string) => void;
  /** Applies a status effect to a specific enemy by ID. */
  readonly applyStatusToEnemy?: (
    enemyId: string,
    statusId: string,
    opts: { durationMs: number; sourceSkillId: string },
  ) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface TemperConfig {
  /** Cooldown in ms after activation, fizzle, or no-burning failure. Default: 2000 (long tier). */
  readonly cooldownMs: number;
}

const DEFAULT_CONFIG: TemperConfig = {
  cooldownMs: 2000,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class TemperSkillEffect implements SkillEffect {
  readonly skillId = 'temper';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: TemperDeps;
  private readonly config: TemperConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzles = 0;
  private _outOfRangeAttempts = 0;
  private _noBurningAttempts = 0;
  private _contagionTransfers = 0;

  constructor(deps: TemperDeps, config: Partial<TemperConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Observable state ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get outOfRangeAttempts(): number { return this._outOfRangeAttempts; }
  get noBurningAttempts(): number { return this._noBurningAttempts; }
  get contagionTransfers(): number { return this._contagionTransfers; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Range check — out-of-range does not consume cooldown
    if (!this.deps.isInMeleeRange()) {
      this._outOfRangeAttempts++;
      return false;
    }

    // Burning precondition — target must already be on fire. Cooldown consumed.
    if (!this.deps.hasTargetStatus('burning')) {
      this._noBurningAttempts++;
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Fizzle check — cooldown consumed even on fizzle
    if (!this.deps.rollSkillSuccess('temper')) {
      this._fizzles++;
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Make the target's burning DoT permanent
    this.deps.makeBurningPermanent();

    // Apply the tempered marker (permanent until death)
    this.deps.applyTargetStatus('tempered', {
      durationMs: -1,
      sourceSkillId: 'temper',
    });

    // Branded upgrade: apply fire_resistance_debuff if unlocked
    if (this.deps.resolver.hasEffect('temper', 'fireResistanceReduction')) {
      this.deps.applyTargetStatus('fire_resistance_debuff', {
        durationMs: -1,
        sourceSkillId: 'temper',
      });
    }

    this._activations++;
    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void { /* instant — no-op */ }

  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  // ---------------------------------------------------------------------------
  // Death trigger — Contagion upgrade
  // ---------------------------------------------------------------------------

  /**
   * Called by the game layer when an enemy dies. When the Contagion upgrade
   * (`transferRadiusTiles`) is unlocked, a Tempered enemy's death propagates
   * permanent burning to the nearest burning enemy within range. The new host
   * receives the `tempered` marker — enabling chain contagion — and, if
   * Branded is also unlocked, the `fire_resistance_debuff`.
   */
  notifyEnemyDeath(context: TemperDeathContext): void {
    // Gate: Contagion must be unlocked
    if (!this.deps.resolver.hasEffect('temper', 'transferRadiusTiles')) return;

    // Dying enemy must carry the tempered marker
    if (!context.activeStatuses.includes('tempered')) return;

    // All three Contagion deps must be wired
    if (!this.deps.findNearestEnemyWithStatus) return;
    if (!this.deps.makeBurningPermanentOnEnemy) return;
    if (!this.deps.applyStatusToEnemy) return;

    const radius =
      this.deps.resolver.getAdditiveEffect?.('temper', 'transferRadiusTiles') ?? 3;

    // Find the nearest burning enemy within propagation range
    const newHostId = this.deps.findNearestEnemyWithStatus(
      context.deathPosition,
      'burning',
      radius,
    );
    if (newHostId === null) return;

    // Make the new host's burning permanent
    this.deps.makeBurningPermanentOnEnemy(newHostId);

    // Apply tempered marker — enables chain contagion on this host's death
    this.deps.applyStatusToEnemy(newHostId, 'tempered', {
      durationMs: -1,
      sourceSkillId: 'temper',
    });

    // Branded: also transfer fire_resistance_debuff if unlocked
    if (this.deps.resolver.hasEffect('temper', 'fireResistanceReduction')) {
      this.deps.applyStatusToEnemy(newHostId, 'fire_resistance_debuff', {
        durationMs: -1,
        sourceSkillId: 'temper',
      });
    }

    this._contagionTransfers++;
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
